import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../../api/client'
import type { AccessRilevamento } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import { Finestra } from '../ModificheGitlab'
import { BadgeGravita } from './comuni'
import { colonneEventi } from './Eventi'

/**
 * «Rilevamenti» of CENSO ACCESS (delivery 4): what the correlations found —
 * off hours, brute force, password spray, success after failures, lateral
 * movement, shared account, first access to a system, dormant accounts, new
 * country, no MFA, new privileges, cleared logs, attempts on disabled
 * accounts. A finding opens with the events behind it; the officer takes it
 * in charge or closes it with a note, which goes in the register.
 */

const REGOLE: Record<string, () => string> = {
  off_hours_privileged: () => t('Accesso privilegiato fuori orario'),
  brute_force: () => t('Forza bruta'),
  password_spray: () => t('Password spray'),
  success_after_failures: () => t('Accesso riuscito dopo una serie di fallimenti'),
  lateral_movement: () => t('Movimento laterale'),
  shared_account: () => t('Account condiviso'),
  first_time_host: () => t('Primo accesso a un sistema'),
  dormant_reactivated: () => t('Account privilegiato dormiente di nuovo in uso'),
  dormant_privileged: () => t('Account privilegiato dormiente'),
  new_country: () => t('Accesso da un Paese nuovo'),
  admin_without_mfa: () => t('Accesso amministrativo senza MFA'),
  new_privilege: () => t('Nuovo privilegio'),
  created_and_promoted: () => t('Account creato e reso privilegiato'),
  log_cleared: () => t('Registro di sicurezza cancellato'),
  audit_change: () => t('Politica di audit modificata'),
  disabled_account: () => t('Tentativi su un account disabilitato'),
}

export function etichettaRegola(r: string): string {
  return REGOLE[r]?.() ?? r
}

const STATI: Record<AccessRilevamento['status'], () => string> = {
  open: () => t('aperto'),
  acknowledged: () => t('preso in carico'),
  closed: () => t('chiuso'),
}

function Dettaglio({ id, scrive, onChiudi, onCambiato }: {
  id: string; scrive: boolean; onChiudi: () => void; onCambiato: () => void
}) {
  const dati = useAsync(() => api.accessDetection(id), [id])
  const [nota, setNota] = useState('')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const d = dati.data

  async function cambia(stato: AccessRilevamento['status']) {
    setBusy(true)
    setErrore(null)
    try {
      await api.accessDetectionSet(id, stato, nota.trim() || undefined)
      setNota('')
      void dati.reload()
      onCambiato()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Finestra titolo={d ? `${d.code} · ${etichettaRegola(d.rule)}` : t('Rilevamento')} sotto={d?.title} onChiudi={onChiudi}>
      {dati.loading && !d ? <Spinner /> : !d ? <ErrorBanner error={dati.error} /> : (
        <>
          <dl className="kv kv--attributi">
            <dt>{t('Gravità')}</dt><dd><BadgeGravita g={d.severity} /></dd>
            <dt>{t('Stato')}</dt><dd>{STATI[d.status]()}{d.status_by && <span className="muted"> · {d.status_by}</span>}</dd>
            <dt>{t('Prima volta')}</dt><dd>{formatDateTime(d.first_at)}</dd>
            <dt>{t('Ultima volta')}</dt><dd>{formatDateTime(d.last_at)} <span className="muted">· {t('{n} eventi', { n: d.count })}</span></dd>
            {d.identity_id && <><dt>{t('Persona')}</dt><dd><Link to={`/access/people/${d.identity_id}`}>{d.identity_name ?? t('apri la scheda')}</Link></dd></>}
            {d.status_note && <><dt>{t('Note')}</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{d.status_note}</dd></>}
          </dl>
          {scrive && (
            <div className="form-grid" style={{ marginTop: 8 }}>
              <label className="field span-6"><span className="field__label">{t('Nota (resta nel registro)')}</span>
                <textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} maxLength={2000} /></label>
              <div className="span-6" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {d.status !== 'acknowledged' && <button type="button" onClick={() => void cambia('acknowledged')} disabled={busy}>{t('Prendi in carico')}</button>}
                {d.status !== 'closed' && <button type="button" className="secondary" onClick={() => void cambia('closed')} disabled={busy}>{t('Chiudi il rilevamento')}</button>}
                {d.status === 'closed' && <button type="button" className="secondary" onClick={() => void cambia('open')} disabled={busy}>{t('Riapri')}</button>}
              </div>
            </div>
          )}
          <ErrorBanner error={errore} />
          <h4 className="form__sezione">{t('Gli eventi')}</h4>
          {d.events.length === 0 ? (
            <p className="muted">{d.rule === 'dormant_privileged' ? t('Nessun evento: è proprio l’assenza di accessi a contare.') : t('Gli eventi sono usciti dalla conservazione.')}</p>
          ) : (
            <DataTable rows={d.events} columns={colonneEventi()} rowKey={(e) => String(e.seq)} conteggio={{ nome: t('eventi') }} paginazione={{ predefinita: 10 }}
                       rowClassName={(e) => (e.outcome === 'failure' ? 'accriga--ko' : e.off_hours && e.privileged ? 'accriga--fuori' : undefined)} />
          )}
          {d.missing_events > 0 && <p className="field__hint">{t('{n} eventi non ci sono più: usciti dalla conservazione.', { n: d.missing_events })}</p>}
        </>
      )}
    </Finestra>
  )
}

export function VistaRilevamenti({ scrive }: { scrive: boolean }) {
  const [parametri, setParametri] = useSearchParams()
  const stato = parametri.get('stato') ?? 'open'
  const persona = parametri.get('persona') ?? undefined
  const dati = useAsync(() => api.accessDetections({ status: stato === 'tutti' ? undefined : stato, identity: persona }), [stato, persona])
  const [aperto, setAperto] = useState<string | null>(null)
  const righe = dati.data?.detections ?? []

  function imposta(v: string) {
    const q = new URLSearchParams(parametri)
    q.set('stato', v)
    setParametri(q, { replace: true })
  }

  return (
    <Card title={t('Rilevamenti')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Le correlazioni leggono il registro in ordine e confrontano ogni evento con quelli appena prima e con la storia dello stesso account, con le soglie delle Impostazioni. Un rilevamento è uno per regola e soggetto (l’account, il giorno): cresce finché il fatto continua e si riapre se torna dopo essere stato chiuso. I nuovi, di gravità media o più, vanno per posta ai destinatari delle Impostazioni.')}
      </p>
      <div className="accfiltri">
        {(['open', 'acknowledged', 'closed', 'tutti'] as const).map((s) => (
          <button key={s} type="button" className={stato === s ? '' : 'secondary'} onClick={() => imposta(s)}>
            {s === 'tutti' ? t('tutti') : STATI[s]()}
          </button>
        ))}
        {persona && <span className="muted">{t('di una persona')} · <Link to="/access/detections">{t('togli il filtro')}</Link></span>}
      </div>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : righe.length === 0 ? (
        <EmptyState title={stato === 'open' ? t('Nessun rilevamento aperto.') : t('Nessun rilevamento.')} hint={t('Le correlazioni girano da sole sugli eventi che arrivano dalle fonti.')} />
      ) : (
        <DataTable rows={righe} rowKey={(d) => d.id} conteggio={{ nome: t('rilevamenti') }} onRowClick={(d) => setAperto(d.id)} columns={[
          { key: 'code', header: t('ID'), mono: true, value: (d) => d.code, render: (d) => <strong className="mono">{d.code}</strong>, nowrap: true },
          { key: 'severity', header: t('Gravità'), filter: 'select', value: (d) => d.severity, render: (d) => <BadgeGravita g={d.severity} /> },
          { key: 'rule', header: t('Rilevamento'), filter: 'select', value: (d) => etichettaRegola(d.rule), render: (d) => <strong>{etichettaRegola(d.rule)}</strong> },
          { key: 'title', header: t('Dettaglio'), value: (d) => d.title },
          { key: 'identity', header: t('Persona'), value: (d) => d.identity_name ?? '' },
          { key: 'count', header: t('Eventi'), type: 'number', value: (d) => d.count },
          { key: 'last_at', header: t('Ultima volta'), type: 'date', value: (d) => d.last_at, render: (d) => formatDateTime(d.last_at) },
          { key: 'status', header: t('Stato'), filter: 'select', value: (d) => STATI[d.status]() },
        ]} />
      )}
      {aperto && <Dettaglio id={aperto} scrive={scrive} onChiudi={() => setAperto(null)} onCambiato={() => void dati.reload()} />}
    </Card>
  )
}
